import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { styled as muiStyled } from "@mui/material/styles";
import {
  Box,
  Typography,
  TablePagination,
  Stack,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import font from "src/styles/font";
import { DarkGrey, Green, Primary, Yellow } from "src/styles/color";
import { useDialog } from "src/context/DialogContext";
import { useAuth } from "src/hooks/useAuth";

import TextField from "src/components/core/TextField";
import Button from "src/components/core/Button";
import Dropdown from "src/components/core/Dropdown";
import CollapsibleTable from "src/components/core/CollapsibleTable";

import {
  deletePromoCode,
  findAllPromoCode,
  updateStatusPromoCode,
} from "src/api/promoCode";
import { convertIntToCurrency } from "src/common";

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const drawerWidth = 260;

const Main = muiStyled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const DrawerHeader = muiStyled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  height: "75px",
}));

function NoRowsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNoData></ImageNoData>
      <TextNoData>No Data</TextNoData>
    </Stack>
  );
}

const PromoCodeList = ({ open }) => {
  const navigate = useNavigate();
  const { checkAllowPermission } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const { openDialog, closeDialog } = useDialog();

  const [promoCodes, setPromoCodes] = useState({
    data: [],
    total: 0,
  });

  const [dataSearch, setDataSearch] = useState({
    page: 0,
    page_size: 10,
    sortBy: "updatedAt",
    sortOrder: "desc",
    fromDate: null,
    toDate: null,
    keyword: "",
    type: "all", // all, product, bill, delivery
  });

  const columns = [
    {
      field: "promo_name",
      label: "ชื่อ/โค้ดส่วนลด",
      width: "20%",
      sortable: false,
      align: "left",
      userRoles: [1],
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Stack
              gap={1}
              sx={{
                "& .MuiTypography-root": {
                  fontSize: font.size.sm,
                },
                "& span.MuiTypography-root": {
                  color: DarkGrey,
                },
              }}
            >
              <Typography component="p">{params?.row?.promo_name}</Typography>
              <Typography component="span">
                {params?.row?.promo_code}
              </Typography>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "promo_type",
      label: "ประเภท",
      // width: "13%",
      sortable: true,
      align: "left",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {params?.row?.promo_type === "product"
              ? "ส่วนลดสินค้า"
              : params?.row?.promo_type === "bill"
                ? "ส่วนลดท้ายบิล"
                : params?.row?.promo_type === "delivery"
                  ? "ส่วนลดค่าขนส่ง"
                  : "ทั้งหมด"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "promo_discount_value",
      label: "มูลค่าส่วนลด/สูงสุด",
      // width: "13%",
      sortable: false,
      // align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {`${
              params?.row?.promo_discount?.promo_discount_type === "percent"
                ? `${
                    params?.row?.promo_discount?.promo_discount_percent
                  }% / ${convertIntToCurrency(
                    params?.row?.promo_discount?.promo_discount_maximum,
                    false,
                  )} บาท`
                : `${convertIntToCurrency(
                    params?.row?.promo_discount?.promo_discount_value,
                    false,
                  )} บาท / -`
            }`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "amount",
      label: "จำนวนที่ใช้/ส่วนลดทั้งหมด",
      // width: "13%",
      sortable: false,
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            // display: "flex",
            // alignItems: "center",
            // justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {`${convertIntToCurrency(params?.row?.used_amount, false)} / ${
              !!params?.row?.amount
                ? convertIntToCurrency(params?.row?.amount, false)
                : `Unlimited`
            }`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "promo_time",
      label: "ระยะเวลาใช้งาน",
      // width: "13%",
      sortable: false,
      align: "center",
      userRoles: [1],
      renderCell: (params) =>
        `${dayjs(params?.row?.promo_start_time).format("DD/MM/YY")} - ${dayjs(
          params?.row?.promo_expire_time,
        ).format("DD/MM/YY")}`,
    },
    {
      field: "status",
      label: "สถานะ",
      // width: "13%",
      sortable: false,
      align: "left",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            // display: "flex",
            // alignItems: "center",
            // textAlign: "center",
            ".MuiTypography-root": {
              fontSize: font.size.sm,
              textTransform: "capitalize",
              color:
                params?.row?.status === "available"
                  ? Green
                  : params?.row?.status === "schedule"
                    ? Yellow
                    : params?.row?.status === "expired" ||
                        params?.row?.status === "fully redeemed"
                      ? Primary
                      : DarkGrey,
            },
          }}
        >
          {params?.row?.status === "fully redeemed" ? (
            <>
              <Typography component="p">available</Typography>
              <Typography component="p">*Full</Typography>
            </>
          ) : params?.row?.status === "hide" ? (
            <>
              <Typography component="p">available</Typography>
              <Typography component="p">*Hide</Typography>
            </>
          ) : (
            <Typography component="p">{params?.row?.status}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      label: "รายละเอียด",
      width: "10%",
      sortable: false,
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Dropdown
          {...params}
          options={(() => {
            if (
              params?.row?.status === "available" ||
              params?.row?.status === "hide" ||
              params?.row?.status === "fully redeemed"
            ) {
              return [
                {
                  label: "ดูรายละเอียด",
                  startIcon: <VisibilityIcon />,
                  onClick: () => {
                    window.open(
                      `/promo-code/preview/${params?.row?._id}`,
                      "_blank",
                    );
                  },
                },
                // ! for test
                // {
                //   label: "แก้ไขรายละเอียด",
                //   startIcon: <EditRoundedIcon />,
                //   onClick: () =>
                //     navigate(`/promo-code/edit/${params?.row?._id}`),
                // },
                // !
                {
                  label: "คัดลอก",
                  startIcon: <ContentCopyIcon />,
                  disabled: !checkAllowPermission("promo_codes.can_create"),
                  onClick: () =>
                    navigate(`/promo-code/create?copyId=${params?.row?._id}`),
                },
                {
                  label:
                    params?.row?.status === "hide" ? "แสดงโค้ด" : "ซ่อนโค้ด",
                  startIcon: <VisibilityOffIcon />,
                  disabled: !checkAllowPermission("promo_codes.can_edit"),
                  onClick: () => {
                    handleUpdateStatus(params?.row?._id);
                  },
                },
              ];
            } else if (
              params?.row?.status === "schedule" ||
              params?.row?.status === "expired"
            ) {
              return [
                {
                  label: "ดูรายละเอียด",
                  startIcon: <VisibilityIcon />,
                  onClick: () => {
                    window.open(
                      `/promo-code/preview/${params?.row?._id}`,
                      "_blank",
                    );
                  },
                },
                {
                  label: "แก้ไขรายละเอียด",
                  startIcon: <EditRoundedIcon />,
                  disabled: !checkAllowPermission("promo_codes.can_edit"),
                  onClick: () =>
                    navigate(`/promo-code/edit/${params?.row?._id}`),
                },
                {
                  label: "คัดลอก",
                  startIcon: <ContentCopyIcon />,
                  disabled: !checkAllowPermission("promo_codes.can_create"),
                  onClick: () =>
                    navigate(`/promo-code/create?copyId=${params?.row?._id}`),
                },
                {
                  label: "ลบโค้ดส่วนลด",
                  startIcon: <DeleteOutlineRoundedIcon />,
                  disabled: !checkAllowPermission("promo_codes.can_delete"),
                  onClick: () => {
                    openDialog({
                      type: "confirm",
                      title: "คุณต้องการลบโค้ดส่วนลดนี้ใช่หรือไม่?",
                      onConfirm: () => {
                        handleDeleteCode(params?.row?._id);
                        closeDialog();
                      },
                      maxWidth: 342,
                    });
                  },
                },
              ];
            }
          })()}
        />
      ),
    },
  ];

  const visibleColumns = columns.filter((col) =>
    col.userRoles.includes(user?.role_id),
  );

  const handlePageChange = (newPage) => {
    setDataSearch({ ...dataSearch, page: newPage });
  };

  const handleSort = (sortBy) => {
    setDataSearch({
      ...dataSearch,
      sortBy: sortBy,
      sortOrder:
        dataSearch.sortBy === sortBy && dataSearch.sortOrder === "asc"
          ? "desc"
          : "asc",
    });
  };

  const fetchPromoCode = async () => {
    try {
      const { rows, count } = await findAllPromoCode(dataSearch);
      setPromoCodes({
        data: rows,
        total: count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const onSearchButton = () => {
    setDataSearch({ ...dataSearch, page: 0 });
    (async () => {
      const { rows, count } = await findAllPromoCode({
        ...dataSearch,
        page: 0,
      });
      setPromoCodes({
        data: rows,
        total: count,
      });
    })();
  };

  const handleUpdateStatus = async (id) => {
    try {
      await updateStatusPromoCode(id);

      await enqueueSnackbar("ลบโค้ดส่วนลดสำเร็จ", {
        variant: "success",
      });

      await fetchPromoCode();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCode = async (id) => {
    try {
      await deletePromoCode(id);

      await enqueueSnackbar("ลบโค้ดส่วนลดสำเร็จ", {
        variant: "success",
      });

      await fetchPromoCode();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPromoCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPromoCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataSearch.page,
    dataSearch.sortBy,
    dataSearch.sortOrder,
    dataSearch.fromDate,
    dataSearch.toDate,
    dataSearch.type,
  ]);

  return (
    <>
      <Main open={open}>
        <DrawerHeader />
        <BoxWrapper>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mr: 2,
              }}
            >
              <TextField
                id="search-promocode-input"
                placeholder="ค้นจากชื่อ หรือ โค้ดส่วนลด"
                className="TextField-without-border-radius"
                fullWidth
                sx={{
                  maxWidth: 670,
                  flexShrink: 0,
                  marginRight: 2,
                }}
                InputProps={{
                  endAdornment: (
                    <SearchIcon
                      sx={{
                        color: "#e04132",
                        cursor: "pointer",
                      }}
                    />
                  ),
                }}
                onChange={(e) => {
                  setDataSearch({
                    ...dataSearch,
                    keyword: e.target.value,
                  });
                }}
                onKeyUp={(e) => (e.code === "Enter" ? onSearchButton() : null)}
              />
            </Box>

            {checkAllowPermission("promo_codes.can_create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/promo-code/create")}
                size="large"
                sx={{
                  minWidth: 300,
                }}
              >
                สร้างโค้ดส่วนลด
              </Button>
            )}
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  mr: 1,
                  color: DarkGrey,
                }}
                component="span"
              >
                โค้ดส่วนลดทั้งหมด :
              </Typography>
              <Typography
                sx={{
                  color: Primary,
                }}
                component="span"
              >
                {promoCodes.total}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "max-content",
                display: "flex",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <Typography
                sx={{
                  mr: 1,
                  color: DarkGrey,
                }}
                component="span"
              >
                ประเภทโค้ด :
              </Typography>
              <FormControl>
                <Select
                  labelId="promo-type-select-label"
                  id="promo-type-select"
                  value={dataSearch.type}
                  onChange={(e) =>
                    setDataSearch({
                      ...dataSearch,
                      type: e.target.value,
                      page: 0,
                    })
                  }
                >
                  <MenuItem value={"all"}>ทุกประเภท</MenuItem>
                  <MenuItem value={"product"}>ส่วนลดสินค้า</MenuItem>
                  <MenuItem value={"delivery"}>ส่วนลดค่าขนส่ง</MenuItem>
                  <MenuItem value={"bill"}>ส่วนลดท้ายบิล</MenuItem>
                </Select>
              </FormControl>

              <Typography
                sx={{
                  mx: 1,
                  color: DarkGrey,
                }}
                component="span"
              >
                From :
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="start date"
                  inputFormat="YYYY-MM-DD"
                  value={dataSearch?.fromDate}
                  onChange={(newValue) => {
                    setDataSearch({
                      ...dataSearch,
                      fromDate: dayjs(newValue).format("YYYY-MM-DD"),
                      page: 0,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, readOnly: true }}
                      variant="outlined"
                      sx={{
                        maxWidth: 160,
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
              <Typography
                sx={{
                  mx: 1,
                  color: DarkGrey,
                }}
                component="span"
              >
                to
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="end date"
                  inputFormat="YYYY-MM-DD"
                  value={dataSearch?.toDate}
                  onChange={(newValue) =>
                    setDataSearch({
                      ...dataSearch,
                      toDate: dayjs(newValue).format("YYYY-MM-DD"),
                      page: 0,
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, readOnly: true }}
                      variant="outlined"
                      sx={{
                        maxWidth: 160,
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Stack>

          <CollapsibleTable
            rows={promoCodes.data}
            collapsible={false}
            columns={visibleColumns}
            components={{
              NoRowsOverlay,
              // NoResultsOverlay,
            }}
            sortBy={dataSearch.sortBy}
            sortOrder={dataSearch.sortOrder}
            onSort={handleSort}
          />
          <TablePagination
            component="div"
            count={promoCodes.total}
            rowsPerPageOptions={[10]} // provide only 1 option to hide rows per page label
            rowsPerPage={10}
            page={dataSearch.page}
            onPageChange={(e, newPage) => handlePageChange(newPage)}
          />
        </BoxWrapper>
      </Main>
    </>
  );
};

export default PromoCodeList;
